import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import UndoIcon from "@mui/icons-material/Undo";
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Divider,
    Grid,
    InputAdornment,
    TextField,
} from "@mui/material";

import * as Yup from "yup";
import { companyNationalIdInputProps, yupCompanyNationalIdWhenLegal } from "../../../../../infrastructure/utils/companyNationalIdRules";
import { wrapFormikHandleChange } from "../../../../../infrastructure/utils/formatters";

import { Formik } from "formik";
import debounce from "lodash/debounce";
import { useEffect, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import DefaultApiService from "../../../../../infrastructure/api/default";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import DropdownPicker from "../../../../components/shared/dropdown";
import FileUploader from "../../../../components/shared/uploader/FileUploader";
import NodeListInput from "./NodeListInput";

const validationSchema = Yup.object().shape({
  cityId: Yup.number().required("لطفا این فیلد را پر کنید."),
  substationId: Yup.number().required("لطفا این فیلد را پر کنید."),
  feederId: Yup.number().required("لطفا این فیلد را پر کنید."),

  projectOwnerFirstName: Yup.string().required("لطفا این فیلد را پر کنید."),
  projectOwnerLastName: Yup.string().required("لطفا این فیلد را پر کنید."),
  projectOwnerMobile: Yup.string().required("لطفا این فیلد را پر کنید."),
  projectOwnerTelephon: Yup.string().required("لطفا این فیلد را پر کنید."),

  projectOwnerAddress: Yup.string().required("لطفا این فیلد را پر کنید."),
  projectTitle: Yup.string().required("لطفا این فیلد را پر کنید."),
  projectAddress: Yup.string().required("لطفا این فیلد را پر کنید."),
  agreementKind: Yup.string().required("لطفا این فیلد را پر کنید."),
  companyNationalCode: yupCompanyNationalIdWhenLegal(),
  companyRegisterationCode: Yup.string().when("agreementKind", {
    is: (val) => val === "Legal",
    then: (s) => s.required("لطفا این فیلد را پر کنید."),
    otherwise: (s) => s.nullable(),
  }),
  postalCode: Yup.string().required("لطفا این فیلد را پر کنید."),
  feederZone: Yup.string().required("لطفا این فیلد را پر کنید."),
  projectCapacity: Yup.number().required("لطفا این فیلد را پر کنید."),
  projectKind: Yup.string().required("لطفا این فیلد را پر کنید."),
  xPoint: Yup.number().required("لطفا این فیلد را پر کنید."),
  yPoint: Yup.number().required("لطفا این فیلد را پر کنید."),
  note: Yup.string().required("لطفا این فیلد را پر کنید."),

  uploadedFileUrl: Yup.string().required("لطفا این فیلد را پر کنید."),

  nodeList: Yup.array()
    .of(
      Yup.object().shape({
        x: Yup.number().required("لطفا این فیلد را پر کنید."),
        y: Yup.number().required("لطفا این فیلد را پر کنید."),
      })
    )
    .test({
      message: "حداقل باید سه نقطه وارد شود",
      test: (arr) => arr.length >= 3,
    }),
});

const SolarentOperatorRequestModifyPage = () => {
  const data = useLoaderData("root");
  let navigate = useNavigate();
  const { id: requestId } = useParams();

  const [loading, setLoading] = useState(false);
  const [feederAvailability, setFeederAvailability] = useState({});

  const onNavigateToListPage = () => {
    navigate("/DG/SolarentOperator/Request");
  };

  // Submit Action
  const onSubmit = async (formValue, { setSubmitting }) => {
    setLoading(true);
    const sendData = { ...data.substation, ...formValue };

    try {
      const res = await DefaultApiService.modifyApi(
        "DG/SolarentOperator/Request",
        sendData
      );

      if (res.isSuccess) {
        toast.success(res.message);
        onNavigateToListPage();
      } else {
        toast.error(res.message);
      }

      setLoading(false);
    } catch (error) {
      const errorList = error?.response?.data;
      if (typeof errorList === "object") {
        for (const key in errorList) {
          if (Object.hasOwnProperty.call(errorList, key)) {
            const list = errorList[key];
            toast.error(list[0]);
          }
        }
      } else toast.error("خطایی رخ داده است.");

      setLoading(false);
      setSubmitting(false);
    }
  };

  const onDebounce = debounce((values) => {
    onCheckFeederCapacity(values);
  }, 1000);

  const onCheckFeederCapacity = async (values) => {
    if (values.feederZone && values.projectCapacity && values.feederId) {
      setLoading(true);

      try {
        const res = await DefaultApiService.postApi(
          "DG/Management/FeederAvailableCapacitiesReport/CheckFeedeCapacity",
          {
            feederId: values.feederId,
            requestCapacity: parseFloat(values.projectCapacity),
            feederZone: values.feederZone,
          }
        );
        setFeederAvailability(res);

        setLoading(false);
      } catch (error) {
        setFeederAvailability({ isSuccess: false, message: "خطایی رخ داد!" });

        setLoading(false);
      }
    } else {
      if (!values.feederId) {
        setFeederAvailability({
          isSuccess: false,
          message: "لطفا فیدر را انتخاب کنید.",
        });
      } else if (!values.feederZone) {
        setFeederAvailability({
          isSuccess: false,
          message: "لطفا منطقه فیدر را انتخاب کنید.",
        });
      } else if (!values.projectCapacity) {
        setFeederAvailability({
          isSuccess: false,
          message: "لطفا ظرفیت فیدر را وارد کنید.",
        });
      }
    }
  };

  useEffect(() => {
    if (!feederAvailability?.message) {
      onCheckFeederCapacity(data?.request);
    }
  }, [data, feederAvailability?.message]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12} md={12}>
        <Card>
          <CardHeader
            title={
              requestId !== "create"
                ? `ویرایش درخواست نیروگاه ${data?.request?.projectTitle}`
                : "ثبت درخواست نیروگاه"
            }
            action={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "fit-content",
                }}
              >
                <Button
                  variant="outlined"
                  color="gray"
                  startIcon={<UndoIcon />}
                  onClick={onNavigateToListPage}
                >
                  بازگشت
                </Button>
              </Box>
            }
          />

          <CardContent>
            <Formik
              initialValues={data.request}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => {
                const onMakeFormikVariable = (name) => ({
                  name: name,
                  fullWidth: true,
                  value: values[name],
                  error: errors[name] && touched[name],
                  helperText: errors[name] && touched[name] && errors[name],
                  variant: "outlined",
                });

                return (
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label="نوع درخواست"
                          onChange={(event) => {
                            setFieldValue("agreementKind", event.target.value);
                          }}
                          list={data.agreementKind}
                          filterable={false}
                          {...onMakeFormikVariable("agreementKind")}
                        />
                      </Grid>

                      {values.agreementKind === "Legal" ? (
                        <>
                          <Grid item xs={12} md={6} lg={3}>
                            <TextField
                              label="شناسه ملی شرکت"
                              onChange={wrapFormikHandleChange(handleChange)}
                              inputProps={companyNationalIdInputProps}
                              {...onMakeFormikVariable("companyNationalCode")}
                            />
                          </Grid>

                          <Grid item xs={12} md={6} lg={3}>
                            <TextField
                              label="شماره ثبت شرکت"
                              onChange={wrapFormikHandleChange(handleChange)}
                              {...onMakeFormikVariable(
                                "companyRegisterationCode"
                              )}
                            />
                          </Grid>
                        </>
                      ) : null}

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="نام متقاضی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("projectOwnerFirstName")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="نام خانوادگی متقاضی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("projectOwnerLastName")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="همراه متقاضی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("projectOwnerMobile")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="تلفن متقاضی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("projectOwnerTelephon")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="ایمیل متقاضی"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("projectOwnerEmail")}
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={3}>
                        <TextField
                          label="کد پستی آدرس متقاضی"
                          multiline
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("postalCode")}
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={3}>
                        <TextField
                          label="عنوان پروژه"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("projectTitle")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="کدرهگیری ساتبا/ایران CHP"
                          type="string"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("satbaOrCHPReferenceId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label="منطقه فیدر"
                          onChange={(event) => {
                            setFieldValue("feederZone", event.target.value);
                            onDebounce({
                              ...values,
                              feederZone: event.target.value,
                            });
                          }}
                          list={data.feederZone}
                          filterable={false}
                          {...onMakeFormikVariable("feederZone")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3} lg={3}>
                        <DropdownPicker
                          label="شهرستان"
                          onChange={(event) => {
                            setFieldValue("cityId", event.target.value);
                            setFieldValue("substationId", "");
                            setFieldValue("feederId", "");
                          }}
                          list={data.cityList}
                          optionName="cityName"
                          optionValue="id"
                          {...onMakeFormikVariable("cityId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3} lg={3}>
                        <DropdownPicker
                          label="ایستگاه"
                          onChange={(event) => {
                            setFieldValue("substationId", event.target.value);
                            setFieldValue("feederId", "");
                          }}
                          list={data.substationList.filter(
                            (x) => x.cityId === values.cityId
                          )}
                          optionName="substationName"
                          optionValue="id"
                          {...onMakeFormikVariable("substationId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={3} lg={3}>
                        <DropdownPicker
                          label="فیدر"
                          onChange={(event) => {
                            setFieldValue("feederId", event.target.value);

                            onDebounce({
                              ...values,
                              feederId: event.target.value,
                            });
                          }}
                          list={data.feederList.filter(
                            (x) => x.substationId === values.substationId
                          )}
                          optionName="feederName"
                          optionValue="id"
                          disabled={!values.substationId}
                          {...onMakeFormikVariable("feederId")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="ظرفیت"
                          type="number"
                          onChange={(event) => {
                            handleChange(event);
                            onDebounce({
                              ...values,
                              projectCapacity: event.target.value,
                            });
                          }}
                          {...onMakeFormikVariable("projectCapacity")}
                          InputProps={{
                            endAdornment: values.projectCapacity ? (
                              loading ? (
                                <CircularProgress size={17} />
                              ) : (
                                <InputAdornment position="end">
                                  {feederAvailability.isSuccess ? (
                                    <CheckCircleIcon color="success" />
                                  ) : (
                                    <ErrorIcon color="error" />
                                  )}
                                </InputAdornment>
                              )
                            ) : null,
                          }}
                          error={
                            (errors.projectCapacity &&
                              touched.projectCapacity) ||
                            !feederAvailability.isSuccess
                          }
                          helperText={
                            (errors.projectCapacity &&
                              touched.projectCapacity &&
                              errors.projectCapacity) ||
                            feederAvailability.message
                          }
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <DropdownPicker
                          label="نوع نیروگاه"
                          onChange={(event) => {
                            setFieldValue("projectKind", event.target.value);
                          }}
                          list={data.projectKind}
                          filterable={false}
                          {...onMakeFormikVariable("projectKind")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="مختصات X"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("xPoint")}
                        />
                      </Grid>

                      <Grid item xs={12} md={6} lg={3}>
                        <TextField
                          label="مختصات Y"
                          type="number"
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("yPoint")}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      <Grid item xs={12} md={12} lg={6}>
                        <TextField
                          label="آدرس متقاضی"
                          multiline
                          minRows={2}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("projectOwnerAddress")}
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={6}>
                        <TextField
                          label="آدرس پروژه"
                          multiline
                          minRows={2}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("projectAddress")}
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={12}>
                        <NodeListInput
                          setFieldValue={setFieldValue}
                          touched={touched}
                          {...onMakeFormikVariable("nodeList")}
                        />
                      </Grid>

                      <Grid item xs={12} md={12}>
                        <FileUploader
                          moduleName={"DG"}
                          maxFiles={1}
                          label="فایل "
                          onChange={(list) => {
                            console.log(list);

                            if (list.length > 0) {
                              setFieldValue("uploadedFileUrl", list[0]);
                            } else {
                              setFieldValue("uploadedFileUrl", "");
                            }
                          }}
                          baseurl={data.baseUrl}
                          placeholder={"فایل مورد نظر را انتخاب کنید"}
                          {...onMakeFormikVariable("uploadedFileUrl")}
                        />
                      </Grid>

                      <Grid item xs={12} md={12} lg={12}>
                        <TextField
                          label="یادداشت"
                          multiline
                          minRows={2}
                          onChange={wrapFormikHandleChange(handleChange)}
                          {...onMakeFormikVariable("note")}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={12}
                        container
                        justifyContent={"flex-end"}
                      >
                        <Button
                          disabled={isSubmitting}
                          variant="outlined"
                          size="large"
                          color="gray"
                          sx={{ mx: 1 }}
                          onClick={onNavigateToListPage}
                        >
                          انصراف
                        </Button>

                        <Button
                          disabled={
                            isSubmitting ||
                            loading ||
                            !feederAvailability.isSuccess
                          }
                          variant="contained"
                          type="submit"
                          size="large"
                        >
                          {loading && (
                            <CircularProgress size={16} color="inherit" />
                          )}
                          {requestId !== "create" ? "ثبت" : "ایجاد"}
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SolarentOperatorRequestModifyPage;
